import Service from '@ember/service';
import { SlAlert } from '@shoelace-style/shoelace';
import ENV from 'dashboard/config/environment';
import { type SlVariant } from 'dashboard/types';
import { service } from '@ember/service';
import type { IntlService } from 'ember-intl';

const { notifyHideDelay } = ENV.APP;

// Always escape HTML for text arguments!
function escapeHtml(html: string) {
  const div = document.createElement('div');
  div.textContent = html;
  return div.innerHTML;
}

export default class ToastService extends Service {
  @service declare intl: IntlService;

  /**
   * Dynamically create a toast notification using the Shoelace SlAlert stack.
   * @see https://shoelace.style/components/alert/#creating-toasts-imperatively
   */
  notify(
    message: string,
    variant: SlVariant = 'primary',
    duration = notifyHideDelay || 5000,
  ): SlAlert {
    let icon;
    switch (variant) {
      case 'warning':
        icon = 'exclamation-triangle-fill';
        break;
    }

    // <sl-alert variant="neutral" open closable>
    //   <sl-icon slot="icon" name="gear" class="text-white"></sl-icon>
    //   <strong>Your settings have been updated</strong><br />
    //   Settings will take effect on next login.
    // </sl-alert>

    /* note we're using the standard Shoelace library for the icon (instead
       of library="showbie") */
    const iconHTML = icon
      ? `<sl-icon name="${icon}" slot="icon" class="w-8 h-8"></sl-icon>`
      : '';

    const alert = Object.assign(document.createElement('sl-alert'), {
      variant,
      closable: true,
      duration,
      innerHTML: `
        ${iconHTML}
        ${escapeHtml(message)}
      `,
    });
    alert.classList.add('lbx-toast');

    document.body.append(alert);
    alert.toast();
    return alert;
  }

  /**
   * Displays a generic warning toast with warning icon.
   * @param error The error object itself which is logged to the console.
   * @param errorText A more specific error message that is logged to the console.
   */
  warn(error: unknown, errorText: string) {
    console.debug(errorText, error);
    this.notify(
      this.intl.t('An error has occurred. Try again later.'),
      'warning',
      10000,
    );
  }
}

// Don't remove this declaration: this is what enables TypeScript to resolve
// this service using `Owner.lookup('service:toast')`, as well
// as to check when you pass the service name as an argument to the decorator,
// like `@service('toast') declare altName: ToastService;`.
declare module '@ember/service' {
  interface Registry {
    toast: ToastService;
  }
}
